import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid';

export interface PlayerState {
  id: string;
  name: string;
  level: number;
  experience: number;
  job: string;
  stats: {
    hp: number;
    attack: number;
    defense: number;
    speed: number;
    magicPower: number;
  };
  skills: {
    id: string;
    level: number;
    cooldown: number;
  }[];
  createdAt: number;
  lastLogin: number;
}

const initialState: PlayerState = {
  id: nanoid(),
  name: 'プレイヤー',
  level: 1,
  experience: 0,
  job: 'novice',
  stats: {
    hp: 100,
    attack: 10,
    defense: 5,
    speed: 5,
    magicPower: 5,
  },
  skills: [],
  createdAt: Date.now(),
  lastLogin: Date.now(),
}

export const playerSlice: Slice<PlayerState> = createSlice({
  name: 'player',
  initialState,
  reducers: {
    setPlayerName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    gainExperience: (state, action: PayloadAction<number>) => {
      state.experience += action.payload;
      // レベルアップロジックはここに追加予定
    },
    levelUp: (state) => {
      state.level += 1;
      // ステータス上昇ロジックはここに追加予定
    },
    changeJob: (state, action: PayloadAction<string>) => {
      state.job = action.payload;
    },
    updateStats: (state, action: PayloadAction<Partial<typeof state.stats>>) => {
      state.stats = { ...state.stats, ...action.payload };
    },
    addSkill: (state, action: PayloadAction<{ id: string; level: number }>) => {
      state.skills.push({
        ...action.payload,
        cooldown: 0,
      });
    },
    upgradeSkill: (state, action: PayloadAction<{ id: string; levelIncrease: number }>) => {
      const { id, levelIncrease } = action.payload;
      const skill = state.skills.find((skill: {id: string, level: number, cooldown: number}) => skill.id === id);
      if (skill) {
        skill.level += levelIncrease;
      }
    },
    // 転生後のプレイヤーリセット (一部のステータスは保持)
    transcend: (state, action: PayloadAction<{ inheritedStats?: Partial<typeof state.stats> }>) => {
      const { inheritedStats } = action.payload;
      // 基本情報をリセット
      state.level = 1;
      state.experience = 0;
      state.job = 'novice';
      
      // 基本ステータスをリセットするが、転生ボーナスを適用
      state.stats = {
        hp: 100,
        attack: 10,
        defense: 5,
        speed: 5,
        magicPower: 5,
        ...inheritedStats
      };
      
      // スキルをリセット
      state.skills = [];
      
      // 最終ログイン更新
      state.lastLogin = Date.now();
    },
  },
});

export const { 
  setPlayerName, 
  gainExperience, 
  levelUp, 
  changeJob, 
  updateStats, 
  addSkill, 
  upgradeSkill,
  transcend
} = playerSlice.actions;

export const playerReducer = playerSlice.reducer; 